import { ChevronLeft, ChevronRight } from "lucide-react";
import { useId, type ReactNode } from "react";
import Button from "@/components/ui/Button";
import { cardShellClass } from "@/components/ui/Card";
import Label from "@/components/ui/Label";
import { fieldControlClass } from "@/components/ui/field-variants";
import { cn } from "@/lib/utils";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

export type PaginationLabels = {
  nextPage: string;
  previousPage: string;
  rowsPerPage: string;
  pageOf: (page: number, totalPages: number) => string;
  paginationSummary: (start: number, end: number, total: number) => string;
};

export interface PaginationProps {
  labels: PaginationLabels;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  total?: number;
  start?: number;
  end?: number;
  summary?: ReactNode;
  pageSize?: number;
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (pageSize: number) => void;
  showPageNumbers?: boolean;
  placement?: "top" | "bottom" | "standalone";
  className?: string;
}

function visiblePageNumbers(page: number, totalPages: number): number[] {
  const windowSize = Math.min(5, totalPages);
  if (totalPages <= windowSize) {
    return Array.from({ length: windowSize }, (_, i) => i + 1);
  }
  if (page <= 3) {
    return Array.from({ length: windowSize }, (_, i) => i + 1);
  }
  if (page >= totalPages - 2) {
    return Array.from(
      { length: windowSize },
      (_, i) => totalPages - windowSize + 1 + i,
    );
  }
  return Array.from({ length: windowSize }, (_, i) => page - 2 + i);
}

export function Pagination({
  labels,
  page,
  totalPages,
  onPageChange,
  isLoading = false,
  total,
  start,
  end,
  summary,
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageSizeChange,
  showPageNumbers = false,
  placement = "standalone",
  className,
}: PaginationProps) {
  const pageSizeSelectId = useId();
  const resolvedTotalPages = Math.max(1, totalPages);
  const atStart = page <= 1;
  const atEnd = page >= resolvedTotalPages;

  const resolvedSummary =
    summary ??
    (start != null && end != null && total != null
      ? labels.paginationSummary(start, end, total)
      : total != null
        ? labels.pageOf(page, resolvedTotalPages)
        : null);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-x-3 gap-y-2 bg-surface-0 px-4 py-2.5 text-[13px] text-surface-500",
        placement === "top" && "border-b border-surface-200",
        placement === "bottom" && "border-t border-surface-200",
        placement === "standalone" && cardShellClass,
        className,
      )}
    >
      {resolvedSummary ? <p className="tabular-nums">{resolvedSummary}</p> : <span />}
      <div className="flex flex-wrap items-center gap-3">
        {onPageSizeChange && pageSize != null ? (
          <Label
            htmlFor={pageSizeSelectId}
            spacing="none"
            className="flex items-center gap-2 font-normal text-surface-700"
          >
            <span>{labels.rowsPerPage}</span>
            <select
              id={pageSizeSelectId}
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
              className={fieldControlClass({
                size: "sm",
                className: "w-auto px-2",
              })}
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </Label>
        ) : null}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon-sm"
            disabled={atStart || isLoading}
            onClick={() => onPageChange(page - 1)}
            aria-label={labels.previousPage}
            title={labels.previousPage}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Button>
          {showPageNumbers ? (
            <div className="flex items-center gap-1">
              {visiblePageNumbers(page, resolvedTotalPages).map((pageNum) => (
                <button
                  key={pageNum}
                  type="button"
                  disabled={isLoading}
                  onClick={() => onPageChange(pageNum)}
                  className={cn(
                    "flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-[13px] font-medium tabular-nums transition-colors disabled:opacity-50",
                    page === pageNum
                      ? "bg-surface-900 text-surface-0"
                      : "text-surface-600 hover:bg-surface-100",
                  )}
                  aria-current={page === pageNum ? "page" : undefined}
                >
                  {pageNum}
                </button>
              ))}
            </div>
          ) : (
            <span className="min-w-20 text-center tabular-nums text-surface-600">
              {labels.pageOf(page, resolvedTotalPages)}
            </span>
          )}
          <Button
            variant="outline"
            size="icon-sm"
            disabled={atEnd || isLoading}
            onClick={() => onPageChange(page + 1)}
            aria-label={labels.nextPage}
            title={labels.nextPage}
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </div>
    </div>
  );
}
